import { Injectable, Injector } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import {
  CreateFolderAction,
  EditFolderAction,
  FolderInformationAction,
  ManagePermissionsAction,
  ManageRulesAction,
  NavigateRouteAction,
  NodeActionTypes,
  getCurrentFolder,
} from '@alfresco/aca-shared/store';
import { ContentManagementService } from '@adw/projects/aca-content/src/lib/services/content-management.service';
import { WkContentManagementService } from '../../services/wk-content-management.service';
import { AppStore } from '../states/app.state';
import { getAppSelection } from '../selectors/app.custom-selectors';

@Injectable()
export class NodeFolderEffects {
  private readonly contentService: ContentManagementService;
  private readonly wkContentManagementService: WkContentManagementService;

  constructor(
    injector: Injector,
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions
  ) {
    this.contentService = injector.get<ContentManagementService>(
      ContentManagementService
    );
    this.wkContentManagementService = injector.get<WkContentManagementService>(
      WkContentManagementService
    );
  }

  createFolder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CreateFolderAction>(NodeActionTypes.CreateFolder),
        map((action) => {
          if (action?.payload) {
            this.wkContentManagementService.createFolder(action.payload);
          } else {
            this.store
              .select(getCurrentFolder)
              .pipe(take(1))
              .subscribe((node) => {
                if (node?.id) {
                  this.wkContentManagementService.createFolder(node.id);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  editFolder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EditFolderAction>(NodeActionTypes.EditFolder),
        map((action) => {
          if (action.payload) {
            this.wkContentManagementService.editFolder(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection?.folder) {
                  this.wkContentManagementService.editFolder(selection.folder);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  managePermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ManagePermissionsAction>(NodeActionTypes.ManagePermissions),
        map((action) => {
          if (action?.payload) {
            this.wkContentManagementService.managePermissions(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && !selection.isEmpty) {
                  this.wkContentManagementService.managePermissions(
                    selection.first
                  );
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  folderInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<FolderInformationAction>(NodeActionTypes.FolderInformation),
        map((action) => {
          if (action?.payload) {
            this.contentService.showFolderInformation(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && !selection.isEmpty && selection.folder.entry) {
                  this.contentService.showFolderInformation(selection.folder);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  manageRules$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ManageRulesAction>(NodeActionTypes.ManageRules),
        map((action) => {
          if (action?.payload) {
            this.store.dispatch(
              new NavigateRouteAction([
                'nodes',
                action.payload.entry.id,
                'rules',
              ])
            );
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && !selection.isEmpty) {
                  this.store.dispatch(
                    new NavigateRouteAction([
                      'nodes',
                      selection.first.entry.id,
                      'rules',
                    ])
                  );
                }
              });
          }
        })
      ),
    { dispatch: false }
  );
}
